import React, { ComponentProps } from 'react';
import { ContentBlockHeadline } from '../../../_lib/components/ContentBlockHeadline';
import { PersonInfoCard, PersonInfoCardModifier } from '../../../components/molecules/PersonInfoCard';
import { PersonInformation } from '../../../components/molecules/PersonInformation';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { asView } from '../../../_lib/ViewFetcher';
import { useBlockContext } from '../../../_lib/BlockFetcher';
import { isNullOrUndefined } from '../../../lib/typeGuards';
import { ExistingUserProfile } from '../../../types/api/UserProfile';
import { SkipLink, SkipLinkTarget } from '../../../_lib/components/SkipLink';

type PersonCollectionLayout = 'card' | 'compact';

type PersonCollectionUser = { id: string; orgUnit: string } & ExistingUserProfile;

type PersonCollectionDefaultProps = {
  title: string;
  persons?: PersonCollectionUser[];
  layout: PersonCollectionLayout;
};

type SkipLinkTarget = 'top' | 'bottom';

function PersonCollectionDefault({ persons, title, layout }: PersonCollectionDefaultProps) {
  const { t } = useSonTranslation('PersonCollectionBlock');
  const { uuid } = useBlockContext();

  if (isNullOrUndefined(persons)) {
    return null;
  }

  const showSkipLinks = persons && persons.length > 3;
  const skipLinkId = (target: SkipLinkTarget) => `person-collection-${target}-${uuid}`;

  const getListItemByType = (person: any) => {
    return layout === 'card' ? (
      <PersonInfoCard
        infoCardData={person}
        modifier={PersonInfoCardModifier.LIGHT}
        stylingContent="normal"
        headlineHierarchy={title ? 3 : 2} // hierarchy depending on headline in block
      />
    ) : (
      <PersonInformation {...person} infoCardUrl={person.personInfoCardUrl} />
    );
  };

  return (
    <>
      <ContentBlockHeadline title={title} />
      {showSkipLinks && (
        <div className="person-collection__skiplinks">
          <SkipLinkTarget id={skipLinkId('top')} targetName={t(`skipLinkTargets.top`)} />
          <SkipLink id={skipLinkId('bottom')}>{t(`skipLinks.bottom`)}</SkipLink>
        </div>
      )}
      {/* Here is no empty ul, because if list is empty, this component is not rendered */}
      <ul className="person-collection">
        {persons.map(person => (
          <li key={person.id} className="person-collection__list-item">
            {getListItemByType(person)}
          </li>
        ))}
      </ul>
      {showSkipLinks && (
        <div className="person-collection__skiplinks">
          <SkipLinkTarget id={skipLinkId('bottom')} targetName={t(`skipLinkTargets.bottom`)} />
          <SkipLink id={skipLinkId('top')}>{t(`skipLinks.top`)}</SkipLink>
        </div>
      )}
    </>
  );
}

export const PersonCollectionDefaultView = asView(PersonCollectionDefault);
export type PersonCollectionDefaultView = ComponentProps<typeof PersonCollectionDefaultView>;
